import { useMemo } from 'react';
import {
  keepPreviousData,
  skipToken,
  useQuery,
  type QueryFunctionContext,
} from '@tanstack/react-query';
import { useCurrentSimDataId } from '../SimViewer/useCurrentSimDataId';
import { dataApiClient } from '../../infrastructure/dataApi/client';
import { useAggregationConfig } from '../SimViewer/useAggregationConfig';
import { useMapVisibility } from '../SimViewer/useMapVisibility';
import { useApiDataFilter } from '../SimViewer/useApiDataFilter';
import { useLinkAggregationFilter } from '../SimViewer/useLinkAggregationFilter';
import type { LinkAggregation } from '../../types/domain';

type UseLinkAggregationData = {
  isLoading: boolean;
  aggregations: Map<string, number>;
  filteredAggregations: Map<string, number>;
};

const EMPTY_DATA = new Map<string, never>();

const useLinkAggregationData = (): UseLinkAggregationData => {
  const { currentSimDataId } = useCurrentSimDataId();
  const { apiFilter } = useApiDataFilter();
  const { aggregationConfig } = useAggregationConfig();
  const { visibleLinkTypes } = useMapVisibility();
  const { linkAggregationFilter } = useLinkAggregationFilter();

  const isRequired = aggregationConfig?.type === 'links';

  const { data, isFetching } = useQuery({
    queryKey: [
      'linkAggregation',
      currentSimDataId,
      apiFilter,
      visibleLinkTypes,
    ],
    queryFn:
      currentSimDataId && isRequired
        ? async ({
            signal,
          }: QueryFunctionContext): Promise<Map<string, number>> => {
            return dataApiClient
              .getLinkAggr(
                currentSimDataId,
                apiFilter,
                'passed_routes_count',
                visibleLinkTypes,
                signal
              )
              .then((items: LinkAggregation[]): Map<string, number> => {
                return new Map<string, number>(
                  items.map((item: LinkAggregation): [string, number] => {
                    return [item.way_id, item.value];
                  })
                );
              });
          }
        : skipToken,
    placeholderData: keepPreviousData,
    meta: { dataType: 'aggregation' },
  });

  const filteredData = useMemo((): Map<string, number> | undefined => {
    // 元データが空、またはフィルタリング条件が存在しない場合は元データを返す
    if (!data?.size || !linkAggregationFilter) {
      return data;
    }
    // フィルタリング条件に基づいてデータをフィルタリングする
    const aggregations = new Map<string, number>();
    for (const [key, value] of data) {
      if (
        linkAggregationFilter[0] <= value &&
        value <= linkAggregationFilter[1]
      ) {
        aggregations.set(key, value);
      }
    }
    return aggregations;
  }, [data, linkAggregationFilter]);

  return {
    isLoading: isFetching,
    aggregations: data && isRequired ? data : EMPTY_DATA,
    filteredAggregations:
      filteredData && isRequired ? filteredData : EMPTY_DATA,
  };
};

export { useLinkAggregationData };
